import { PageMeta, SiteJsonLd } from "@/components/layout/Seo";
import { useSearchParams } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { PullToRefresh } from "@/features/chats/components/PullToRefresh";
import { useAppShell } from "@/lib/useAppShell";
import { EmptyState, GlowButton } from "@/components/ui";
import { useHome } from "@/features/home/api";
import { usePublicConfig } from "@/features/config/api";
import { markHomeReady } from "@/app/introSignals";
import { DesktopHero, MobileAboveFold, TrendingGrid, useTrending } from "@/features/home/AboveFold";
import { useStates } from "@/features/locations/api";
import { useMyState, useShoppingStateId } from "@/features/locations/myState";
import { hasEngaged, track } from "@/lib/analytics";

const HomeBelow = lazy(() => import("@/features/home/HomeBelow"));

export const HOME_TITLE = "RealityStore: Buy & Sell Phones, Cars, Property, Jobs & More in Nigeria";
export const HOME_DESCRIPTION =
  "Nigeria's safe marketplace. Buy and sell phones, cars, homes, solar, jobs and services. Chat with sellers and pay with RealitySafe: your money is held until you get your item.";

/**
 * The rest of the page mounts only once the visitor heads down (scroll, or the area comes near the
 * viewport), so the first screen is interactive sooner on mid-range phones.
 */
function BelowFoldGate({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (show) return undefined;
    const go = () => setShow(true);
    const el = ref.current;
    const io =
      el && typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && go(), { rootMargin: "0px" })
        : null;
    if (el && io) io.observe(el);
    else go();
    window.addEventListener("scroll", go, { once: true, passive: true });
    window.addEventListener("keydown", go, { once: true });
    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", go);
      window.removeEventListener("keydown", go);
    };
  }, [show]);
  return show ? <>{children}</> : <div ref={ref} className="min-h-[60vh]" aria-hidden />;
}

/** Scroll depth (25/50/75/100) once per visit, and a bounce if the visitor leaves without engaging. */
function useHomeAnalytics() {
  useEffect(() => {
    track("home_view", { theme: document.documentElement.dataset.theme ?? "light", device: window.innerWidth < 768 ? "phone" : "desktop" });
    const sent = new Set<number>();
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const pct = max > 0 ? (window.scrollY / max) * 100 : 100;
        [25, 50, 75, 100].forEach((d) => {
          if (pct >= d - 1 && !sent.has(d)) {
            sent.add(d);
            track("home_scroll_depth", { depth: d });
          }
        });
      });
    };
    const onHide = () => {
      if (!hasEngaged() && window.location.pathname === "/") track("home_bounce");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onHide);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onHide);
    };
  }, []);
}

export function HomePage() {
  const stateId = useShoppingStateId();
  const mine = useMyState();
  const states = useStates();
  const stateName = mine?.name ?? states.data?.find((st) => st.id === stateId)?.name ?? null;
  const [params] = useSearchParams();
  const preview = params.get("home_preview");
  const home = useHome(stateId, preview);
  const trending = useTrending(stateId, 12);
  const config = usePublicConfig().data;
  const adEvery = Math.max(1, config?.ad_infeed_every_sections ?? 3);
  const shell = useAppShell();
  const desktop = shell.breakpoint === "desktop";
  const qc = useQueryClient();
  useHomeAnalytics();
  useEffect(() => {
    if (home.isSuccess || home.isError) markHomeReady();
  }, [home.isSuccess, home.isError]);

  const sections = home.data?.sections ?? [];
  const hero = sections.find((s) => s.type === "HERO_BANNERS");
  const trendingTitle = trending.local && stateName ? `Trending near you · ${stateName}` : "Trending in Nigeria";
  const refresh = () =>
    Promise.all([home.refetch(), qc.invalidateQueries({ queryKey: ["products"] }), qc.invalidateQueries({ queryKey: ["ads"] })]);

  const page = (
    <div className="relative flex flex-col gap-8 pb-10 sm:gap-12">
      {desktop ? (
        <div className="flex flex-col gap-8 pt-5">
          <DesktopHero banners={hero?.banners ?? []} stateId={stateId} featured={trending.items} />
          <div id="trending" className="scroll-mt-28">
            <TrendingGrid items={trending.items} loading={trending.loading} title={trendingTitle} />
          </div>
        </div>
      ) : (
        <MobileAboveFold stateName={stateName} trending={trending} />
      )}
      {home.isError && (
        <EmptyState
          icon={<Sparkles />}
          title="We couldn't load the rest of the page"
          description="Check your connection and try again."
          action={<GlowButton onClick={() => void home.refetch()}>Retry</GlowButton>}
        />
      )}
      <BelowFoldGate>
        <Suspense fallback={<div className="min-h-[60vh]" aria-hidden />}>
          <HomeBelow
            sections={sections}
            stateId={stateId}
            desktop={desktop}
            adEvery={adEvery}
            videoUrl={config?.how_it_works_video_url || undefined}
          />
        </Suspense>
      </BelowFoldGate>
    </div>
  );

  return (
    <>
      <PageMeta title={HOME_TITLE} description={HOME_DESCRIPTION} image="/og-image.jpg" />
      <SiteJsonLd />
      {preview && (
        <div className="sticky top-0 z-50 bg-warning/90 px-4 py-2 text-center text-sm font-medium text-void" role="status">
          Preview of an unpublished home layout
        </div>
      )}
      {shell.isMobileShell ? (
        <PullToRefresh onRefresh={refresh} className="overflow-visible">
          {page}
        </PullToRefresh>
      ) : (
        page
      )}
    </>
  );
}
